/**
 * The Meetings tab's styles (#644). `mtg-` is a naming convention, not a scope:
 * this is an ordinary global stylesheet, mounted by the tab.
 */
const css = `
  .mtg-pad { padding: 10px var(--ew-s-4); margin: 0; }
  .mtg-muted { color: var(--ew-n-500); }
  .mtg-small { font-size: 12px; }
  .mtg-error { color: var(--ew-danger-700); background: var(--ew-danger-50); border-radius: var(--ew-r-sm); padding: 8px 10px; margin: 0; }
  .mtg-hd { display: flex; align-items: center; gap: 12px; padding-right: var(--ew-s-4); border-bottom: 1px solid var(--ew-n-200); flex-wrap: wrap; }
  .mtg-hd .pw-card-hd { flex: 1; border: 0; }
  /* "← All meetings", as RFIs' back link is. Not btn-ghost: that is the dark
     top bar's white-on-transparent style, and it was invisible here. */
  .mtg-back {
    background: transparent; border: 0; cursor: pointer; padding: 0;
    color: var(--ew-n-500); font: var(--ew-fw-medium) var(--ew-fs-body-sm) var(--ew-font);
    transition: color var(--ew-dur-fast) var(--ew-ease);
  }
  .mtg-back:hover { color: var(--ew-dark); }

  /* The list */
  .mtg-list { list-style: none; margin: 0; padding: 0; }
  .mtg-row {
    display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1.2fr) minmax(0, 1fr) 110px 120px;
    gap: 12px; align-items: center; padding: 10px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100);
    font-size: var(--ew-fs-body-sm); width: 100%; text-align: left; background: none; border-left: 0; border-right: 0; border-top: 0;
    cursor: pointer; font-family: inherit; color: inherit;
  }
  .mtg-row:hover { background: var(--ew-n-50); }
  .mtg-row:focus-visible { outline: 2px solid var(--ew-green); outline-offset: -2px; }
  .mtg-name { font-weight: var(--ew-fw-semibold); color: var(--ew-dark); overflow-wrap: anywhere; }
  .mtg-count { font-variant-numeric: tabular-nums; color: var(--ew-n-700); }
  @media (max-width: 760px) { .mtg-row { grid-template-columns: 1fr; gap: 2px; } }

  /* The editor */
  .mtg-form { display: grid; gap: var(--ew-s-4); }
  .mtg-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px; padding: var(--ew-s-4); }
  .mtg-field { display: grid; gap: 4px; font-size: 13px; align-content: start; }
  .mtg-field > span { font-weight: var(--ew-fw-medium); color: var(--ew-n-700); }
  .mtg-field input, .mtg-field select, .mtg-field textarea, .mtg-input {
    font: inherit; padding: 7px 9px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-sm); background: #fff; min-width: 0;
  }
  .mtg-field textarea { min-height: 64px; resize: vertical; }
  .mtg-field input:disabled, .mtg-field select:disabled, .mtg-field textarea:disabled, .mtg-input:disabled { background: var(--ew-n-50); color: var(--ew-n-700); }
  .mtg-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .mtg-bar .mtg-spacer { flex: 1; }

  .mtg-att { display: grid; grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr) 130px 32px; gap: 8px; align-items: center; padding: 6px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); }
  .mtg-att-name { font-weight: var(--ew-fw-medium); overflow-wrap: anywhere; }
  .mtg-add { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; padding: 10px var(--ew-s-4); }
  .mtg-add select, .mtg-add input { font: inherit; padding: 6px 9px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-sm); background: #fff; min-width: 0; }
  @media (max-width: 640px) { .mtg-att { grid-template-columns: 1fr 32px; } }

  .mtg-item { border-bottom: 1px solid var(--ew-n-100); padding: 12px var(--ew-s-4); display: grid; gap: 8px; }
  .mtg-item-hd { display: grid; grid-template-columns: 34px minmax(0, 1fr) auto; gap: 8px; align-items: start; }
  .mtg-num { font: var(--ew-fw-semibold) 13px/34px var(--ew-font); font-variant-numeric: tabular-nums; color: var(--ew-n-500); }
  .mtg-item-meta { display: grid; grid-template-columns: minmax(0, 1fr) 130px minmax(0, 1fr) 150px; gap: 8px; padding-left: 42px; }
  .mtg-item-minutes { padding-left: 42px; }
  @media (max-width: 760px) {
    .mtg-item-meta { grid-template-columns: 1fr 1fr; padding-left: 0; }
    .mtg-item-minutes { padding-left: 0; }
  }
  .mtg-icon { border: 1px solid transparent; background: none; cursor: pointer; font: var(--ew-fw-medium) 13px/1 var(--ew-font); color: var(--ew-n-700); padding: 6px 7px; border-radius: var(--ew-r-sm); }
  .mtg-icon:hover:not(:disabled) { background: var(--ew-n-100); }
  .mtg-icon:disabled { opacity: 0.35; cursor: default; }
  .mtg-icons { display: flex; gap: 2px; }

  /* Read-only: a viewer's copy of the minutes */
  .mtg-read-item { padding: 12px var(--ew-s-4); border-bottom: 1px solid var(--ew-n-100); display: grid; grid-template-columns: 34px minmax(0, 1fr); gap: 8px; }
  .mtg-read-title { font-weight: var(--ew-fw-semibold); color: var(--ew-dark); }
  .mtg-read-meta { display: flex; flex-wrap: wrap; gap: 4px 14px; font-size: 12.5px; color: var(--ew-n-700); margin-top: 3px; }
  .mtg-read-minutes { white-space: pre-wrap; margin: 6px 0 0; font-size: var(--ew-fs-body-sm); }
  .mtg-section { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption); text-transform: uppercase; color: var(--ew-green-700); padding: 14px var(--ew-s-4) 4px; }
`;

export function MeetingStyles() {
  return <style>{css}</style>;
}
